/**
 * Live exchange rates from the free, key-less ExchangeRate-API open endpoint.
 * Base currency is fixed to Malaysian Ringgit (MYR) so every rate answers
 * "how many units of X equals 1 MYR".
 *
 * This is a third-party public API, so it is called with plain `fetch`
 * rather than the authenticated axios instance (no bearer token should leak).
 */

export const BASE_CURRENCY = "MYR";

const OPEN_EXCHANGE_RATE_URL = `https://open.er-api.com/v6/latest/${BASE_CURRENCY}`;

export interface ExchangeRateSnapshot {
  /** Units of each currency that equal 1 MYR, keyed by ISO 4217 code. */
  rates: Record<string, number>;
  /** When the provider last refreshed the rates. */
  lastUpdated: Date | null;
  /** When the provider will refresh next. */
  nextUpdate: Date | null;
}

export interface CurrencyOption {
  code: string;
  name: string;
  flag: string;
}

interface OpenErApiResponse {
  result: "success" | "error";
  "error-type"?: string;
  time_last_update_unix?: number;
  time_next_update_unix?: number;
  base_code?: string;
  rates?: Record<string, number>;
}

const currencyNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "currency" })
    : null;

/** Currency codes whose first two letters are not a country (no flag). */
const NON_COUNTRY_PREFIXES = new Set(["XA", "XC", "XD", "XO", "XP"]);

function flagFromCurrencyCode(code: string): string {
  const prefix = code.slice(0, 2).toUpperCase();
  if (prefix.length !== 2 || NON_COUNTRY_PREFIXES.has(prefix)) return "🏳️";
  const first = prefix.charCodeAt(0);
  const second = prefix.charCodeAt(1);
  if (first < 65 || first > 90 || second < 65 || second > 90) return "🏳️";
  return String.fromCodePoint(0x1f1e6 + (first - 65), 0x1f1e6 + (second - 65));
}

export function describeCurrency(code: string): CurrencyOption {
  let name = code;
  try {
    const resolved = currencyNames?.of(code);
    if (resolved && resolved !== code) name = resolved;
  } catch {
    // Unknown code for the Intl database; fall back to the raw code.
  }
  return { code, name, flag: flagFromCurrencyCode(code) };
}

export async function fetchMyrExchangeRates(signal?: AbortSignal): Promise<ExchangeRateSnapshot> {
  const response = await fetch(OPEN_EXCHANGE_RATE_URL, { signal });
  if (!response.ok) {
    throw new Error(`Exchange rate service returned ${response.status}`);
  }

  const body = (await response.json()) as OpenErApiResponse;
  if (body.result !== "success" || !body.rates) {
    throw new Error(body["error-type"] || "Exchange rate service returned an error");
  }

  return {
    rates: body.rates,
    lastUpdated: body.time_last_update_unix ? new Date(body.time_last_update_unix * 1000) : null,
    nextUpdate: body.time_next_update_unix ? new Date(body.time_next_update_unix * 1000) : null,
  };
}

/**
 * Convert an amount in `fromCode` to MYR using a MYR-based rate table.
 * Returns null when the currency is unknown or the rate is unusable.
 */
export function convertToMyr(
  amount: number,
  fromCode: string,
  rates: Record<string, number>
): number | null {
  const rate = rates[fromCode];
  if (!Number.isFinite(amount) || !rate || rate <= 0) return null;
  return amount / rate;
}
